import React from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, MessageCircle, Instagram, Linkedin, Facebook } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { COMPANY, TOPICS } from "../lib/data";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";

const API = process.env.REACT_APP_API_URL || `${process.env.REACT_APP_BACKEND_URL}/api`;

// Load reCAPTCHA v3 script if a site_key is configured
async function loadRecaptcha(siteKey) {
  if (!siteKey || window.grecaptcha) return;
  await new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    s.async = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export default function Contact() {
  const { lang } = useApp();
  const [form, setForm] = React.useState({ name: "", phone: "", email: "", city: "", topic: "", message: "" });
  const [loading, setLoading] = React.useState(false);
  const [recaptchaSiteKey, setRecaptchaSiteKey] = React.useState("");

  React.useEffect(() => {
    axios.get(`${API}/config/recaptcha`).then((r) => {
      if (r.data?.enabled && r.data?.site_key) {
        setRecaptchaSiteKey(r.data.site_key);
        loadRecaptcha(r.data.site_key).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let recaptcha_token = null;
      if (recaptchaSiteKey && window.grecaptcha) {
        await new Promise((res) => window.grecaptcha.ready(res));
        recaptcha_token = await window.grecaptcha.execute(recaptchaSiteKey, { action: "contact" });
      }
      await axios.post(`${API}/contact`, { ...form, recaptcha_token });
      toast.success(t(lang, "contact.success"));
      setForm({ name: "", phone: "", email: "", city: "", topic: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again or WhatsApp us.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <p className="overline text-xs uppercase tracking-[0.2em] font-bold text-primary">{t(lang, "contact.title")}</p>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-2">{t(lang, "contact.headline")}</h1>
      <p className="text-base md:text-lg text-muted-foreground mt-4 max-w-2xl">{t(lang, "contact.sub")}</p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-12">
        <Card className="lg:col-span-7 border-border">
          <CardContent className="p-8">
            <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name">{t(lang, "contact.name")}</Label>
                  <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
                </div>
                <div>
                  <Label htmlFor="phone">{t(lang, "contact.phone")}</Label>
                  <Input id="phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" />
                </div>
                <div>
                  <Label htmlFor="email">{t(lang, "contact.email")}</Label>
                  <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
                </div>
                <div>
                  <Label htmlFor="city">{t(lang, "contact.city")}</Label>
                  <Input id="city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="contact-city" />
                </div>
              </div>
              <div>
                <Label htmlFor="topic">{t(lang, "contact.topic")}</Label>
                <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                  <SelectTrigger id="topic" data-testid="contact-topic"><SelectValue placeholder={t(lang, "contact.topicPlaceholder")} /></SelectTrigger>
                  <SelectContent>
                    {TOPICS.map((t0) => <SelectItem key={t0} value={t0}>{t0}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="message">{t(lang, "contact.message")}</Label>
                <Textarea id="message" rows={5} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
              </div>
              <Button type="submit" disabled={loading} className="bg-primary text-primary-foreground hover:brightness-110" data-testid="contact-submit">
                {loading ? t(lang, "contact.sending") : t(lang, "cta.submit")}
              </Button>
              {recaptchaSiteKey && (
                <p className="text-[10px] text-muted-foreground mt-2">
                  This site is protected by reCAPTCHA. Google{" "}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline">Privacy</a>{" "}&{" "}
                  <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline">Terms</a> apply.
                </p>
              )}
            </form>
          </CardContent>
        </Card>

        <div className="lg:col-span-5 space-y-5">
          <Card className="border-border">
            <CardContent className="p-7 space-y-4">
              <div className="flex gap-3"><MapPin className="h-5 w-5 text-primary shrink-0 mt-0.5" /><div><div className="text-xs uppercase tracking-widest text-muted-foreground">{t(lang, "contact.office")}</div><div className="text-sm mt-1">{COMPANY.address}</div></div></div>
              <div className="flex gap-3"><Phone className="h-5 w-5 text-primary shrink-0 mt-0.5" /><div><div className="text-xs uppercase tracking-widest text-muted-foreground">{t(lang, "contact.phoneLabel")}</div><div className="text-sm mt-1"><a href={`tel:${COMPANY.phone}`} className="hover:text-primary" data-testid="contact-phone-link">{COMPANY.phoneDisplay}</a> · <a href={`tel:${COMPANY.landline}`} className="hover:text-primary">{COMPANY.landline}</a></div></div></div>
              <div className="flex gap-3"><Mail className="h-5 w-5 text-primary shrink-0 mt-0.5" /><div><div className="text-xs uppercase tracking-widest text-muted-foreground">{t(lang, "contact.emailLabel")}</div><a href={`mailto:${COMPANY.email}`} className="text-sm mt-1 inline-block hover:text-primary break-all" data-testid="contact-email-link">{COMPANY.email}</a></div></div>
              <div className="flex gap-3"><Clock className="h-5 w-5 text-primary shrink-0 mt-0.5" /><div><div className="text-xs uppercase tracking-widest text-muted-foreground">{t(lang, "contact.hoursLabel")}</div><div className="text-sm mt-1">{t(lang, "contact.hours")}</div></div></div>
              <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn">
                <Button className="w-full bg-[#25D366] text-white hover:bg-[#1da851] mt-2"><MessageCircle className="h-4 w-4 mr-2" /> {t(lang, "contact.whatsapp")}</Button>
              </a>
              <div className="flex gap-3 pt-2">
                <a href={COMPANY.social.instagram} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition" data-testid="contact-instagram"><Instagram className="h-4 w-4" /></a>
                <a href={COMPANY.social.linkedin} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition" data-testid="contact-linkedin"><Linkedin className="h-4 w-4" /></a>
                <a href={COMPANY.social.facebook} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition" data-testid="contact-facebook"><Facebook className="h-4 w-4" /></a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="mt-12 rounded-2xl overflow-hidden border border-border" data-testid="contact-map">
        <iframe
          title="Arthniti Office Map"
          src={COMPANY.mapsEmbed}
          width="100%" height="420" style={{ border: 0 }}
          loading="lazy" referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </div>
  );
}
